import type { StripeElementsOptions } from '@stripe/stripe-js';
import { hexToRgbChannels } from './guestTheme';

type Appearance = StripeElementsOptions['appearance'];

/**
 * Build a Stripe Elements `appearance` from the active guest-portal theme.
 *
 * The Stripe PaymentElement renders inside an iframe, so our CSS variables can't reach it —
 * it's styled only through this JS `appearance` object. We read the resolved `#guest-portal`
 * CSS custom properties (set by GuestThemeProvider, or the built-in defaults when no theme
 * is active) and map them onto Stripe's appearance variables/rules. Falls back to the dark
 * defaults if no `#guest-portal` element is present.
 */
export const buildStripeAppearance = (): Appearance => {
  const el =
    typeof document !== 'undefined' ? document.getElementById('guest-portal') : null;
  const read = (name: string, fallback: string): string =>
    (el ? getComputedStyle(el).getPropertyValue(name).trim() : '') || fallback;

  const inputBg = read('--text-input-bg-color', '#0f0f0f');
  const text = read('--primary-font-color', '#ffffff');
  const secondary = read('--secondary-font-color', '#afafaf');
  const primary = read('--primary-button-color', '#2ce983');
  const border = read('--guest-border-color', '#515151');
  const danger = read('--error-color', '#ff7c7c');

  return {
    theme: 'night',
    variables: {
      colorPrimary: primary,
      colorBackground: inputBg,
      colorText: text,
      colorTextSecondary: secondary,
      colorDanger: danger,
      borderRadius: '0px',
    },
    rules: {
      '.Input': {
        backgroundColor: inputBg,
        border: `1px solid ${border}`,
        color: text,
      },
      // Keep invalid inputs on the themed border rather than Stripe's harsh `colorDanger`
      // outline. Stripe eagerly marks the Link inline fields ("Save my information for
      // faster checkout") invalid while empty, which rendered them with a jarring red
      // border; genuine validation still surfaces via the red `.Error` message text.
      '.Input--invalid': {
        border: `1px solid ${border}`,
        color: text,
        boxShadow: 'none',
      },
      // Mirror the portal's own inputs (GuestPagesModalsV2 styles.css): the placeholder is a
      // faded version of the input TEXT color, not the secondary token. This guarantees it
      // contrasts the input background (the text color is already chosen to), whereas the
      // secondary color could vanish against it (e.g. a light secondary on a light input).
      '.Input::placeholder': {
        color: `rgba(${hexToRgbChannels(text)}, 0.6)`,
      },
      '.Label': {
        color: secondary,
      },
      // Link's opt-in section renders inside a `.Block`, whose default background is
      // Stripe's `colorBackground` — which we map to the themed input color (white on a
      // light-input theme). That white box hid the light Link labels. Keep the block
      // transparent so it inherits the modal's themed background and the labels stay
      // readable, while the inner `.Input` fields keep their own themed surface.
      '.Block': {
        backgroundColor: 'transparent',
      },
    },
  };
};
